import { create } from 'zustand'

import { decodeToken, type TokenPayload } from './tokens'
import type {
  AccessTokenResponse,
  AuthenticatedUser,
  GlobalPermissions,
} from '@/api/types'

/**
 * The signed-in session.
 *
 * Zustand rather than TanStack Query because two things that are not React
 * components read it synchronously on every use: the API middleware, which needs
 * the access token before a request leaves, and the websocket client, which is a
 * module singleton created outside the tree. A Query cache entry is reachable
 * from neither without threading a client through.
 */
export interface SessionState {
  accessToken: string | null
  accessTokenPayload: TokenPayload | null
  /** When this client received the access token, in epoch milliseconds. */
  tokenReceivedAt: number
  refreshToken: string | null
  user: AuthenticatedUser | null
  permissions: GlobalPermissions[]
  /**
   * Unread notifications that belong to the user rather than to a workspace.
   *
   * Arrives with the session and is then kept current by realtime events. The
   * per-workspace counts are not here -- they live on each workspace in the
   * `qk.workspaces()` cache, which is where the server puts them.
   */
  unreadNotificationCount: number
  authenticated: boolean
  /**
   * Identifies this browser tab to the realtime backend, which uses it to avoid
   * echoing a client its own mutations. Generated once per tab and stable for its
   * lifetime -- including across sign-in and sign-out, because it identifies the
   * connection rather than the session.
   */
  webSocketId: string
  /**
   * Groups the actions of one tab for undo/redo. Untrusted: the backend scopes it
   * to the authenticated user, so it is an identifier, not a credential.
   */
  clientSessionId: string
}

export interface SessionActions {
  /** Records a session from an authentication or refresh response. */
  setSession: (
    response: AccessTokenResponse & { refresh_token?: string },
    options?: { receivedAt?: number },
  ) => void
  updateUser: (user: Partial<AuthenticatedUser>) => void
  /**
   * Replaces the permission set wholesale.
   *
   * Replaced rather than merged: the server sends the complete set every time,
   * and merging would keep a permission that was just revoked.
   */
  setPermissions: (permissions: GlobalPermissions[]) => void
  setUnreadNotificationCount: (count: number) => void
  /** Drops the session, keeping the ids that identify this tab. */
  clearSession: () => void
}

function randomId(): string {
  return crypto.randomUUID()
}

const initialState = (): SessionState => ({
  accessToken: null,
  accessTokenPayload: null,
  tokenReceivedAt: 0,
  refreshToken: null,
  user: null,
  permissions: [],
  unreadNotificationCount: 0,
  authenticated: false,
  webSocketId: randomId(),
  clientSessionId: randomId(),
})

export const useSessionStore = create<SessionState & SessionActions>((set, get) => ({
  ...initialState(),

  setSession: (response, options) => {
    const receivedAt = options?.receivedAt ?? Date.now()
    set({
      accessToken: response.access_token,
      accessTokenPayload: decodeToken(response.access_token),
      tokenReceivedAt: receivedAt,
      // A refresh only returns a new refresh token when the backend rotates them.
      // Dropping the existing one on a response that simply omits it would end the
      // session at the next reload.
      refreshToken: response.refresh_token ?? get().refreshToken,
      user: response.user,
      permissions: response.permissions,
      unreadNotificationCount: response.user_notifications.unread_count,
      authenticated: true,
    })
  },

  updateUser: (user) => {
    const current = get().user
    if (current === null) return
    set({ user: { ...current, ...user } })
  },

  setPermissions: (permissions) => set({ permissions }),

  setUnreadNotificationCount: (count) =>
    set({ unreadNotificationCount: Math.max(0, count) }),

  clearSession: () =>
    set({
      accessToken: null,
      accessTokenPayload: null,
      tokenReceivedAt: 0,
      refreshToken: null,
      user: null,
      permissions: [],
      unreadNotificationCount: 0,
      authenticated: false,
    }),
}))

/** Reads the session outside React, for the API middleware and the WS client. */
export function getSession(): SessionState & SessionActions {
  return useSessionStore.getState()
}

/**
 * Test seam: resets to a fresh tab, new ids included.
 *
 * Merged rather than replaced -- a replacing `setState` would drop the actions
 * along with the state, since both live on the one store object.
 */
export function __resetSessionForTests(): void {
  useSessionStore.setState(initialState())
}
